import { useMemo, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CheckCircle, DollarSign, ArrowRight } from 'lucide-react';
import { StatsCard, QuickActions, BookingCard } from '../../../components/Dashboard';
import { useMyBookings } from '../../../hooks/useBookings';
import { formatCurrencyAmount } from '../../../utils/currency';

export const OverviewSection: FC = () => {
  const { data: bookings = [], isLoading, error, refetch } = useMyBookings();

  const { upcomingBookings, completedCount, totalSpent, nextBooking } = useMemo(() => {
    const now = new Date();
    const upcoming = bookings.filter(
      (b) => new Date(b.startTime) > now && b.status !== 'CANCELLED'
    );
    const completed = bookings.filter((b) => b.status === 'COMPLETED').length;
    const spent = bookings
      .filter((b) => b.status === 'COMPLETED')
      .reduce((sum, b) => sum + Number(b.totalAmount), 0);
    return {
      upcomingBookings: upcoming,
      completedCount: completed,
      totalSpent: spent,
      nextBooking: upcoming[0],
    };
  }, [bookings]);

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error.message}</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-6">Welcome Back</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatsCard
          title="Upcoming Bookings"
          value={upcomingBookings.length}
          icon={<Calendar className="w-6 h-6" />}
          color="primary"
          isLoading={isLoading}
        />
        <StatsCard
          title="Completed Sessions"
          value={completedCount}
          icon={<CheckCircle className="w-6 h-6" />}
          color="success"
          isLoading={isLoading}
        />
        <StatsCard
          title="Total Spent"
          value={formatCurrencyAmount(totalSpent)}
          icon={<DollarSign className="w-6 h-6" />}
          color="warning"
          isLoading={isLoading}
        />
      </div>

      {/* Quick Actions */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">Quick Actions</h2>
        <QuickActions />
      </section>

      {/* Next Appointment */}
      {nextBooking && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">Next Appointment</h2>
          <BookingCard booking={nextBooking} />
        </section>
      )}

      {/* Upcoming Bookings Preview */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Upcoming Bookings</h2>
          {upcomingBookings.length > 3 && (
            <Link
              to="/dashboard?section=bookings"
              className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-3 animate-pulse">
                <div className="flex justify-between mb-2">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-slate-700 rounded" />
                  <div className="h-5 w-20 bg-gray-200 dark:bg-slate-700 rounded-full" />
                </div>
                <div className="h-3 w-40 bg-gray-200 dark:bg-slate-700 rounded" />
              </div>
            ))}
          </div>
        ) : upcomingBookings.length > 0 ? (
          <div className="space-y-4">
            {upcomingBookings.slice(0, 3).map((booking) => (
              <BookingCard key={booking.id} booking={booking} variant="compact" />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
            <Calendar className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-slate-400 mb-4">No upcoming bookings</p>
            <Link
              to="/services"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Book a Session
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};
